import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ExternalLink,
  Copy,
} from "lucide-react";
import { toast } from "react-toastify";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";

const CATEGORY_COLORS = {
  onboarding: {
    bg: colors.blue[50],
    color: colors.primary.main,
    label: "Getting Started",
  },
  creating: {
    bg: colors.purple[50],
    color: colors.purple[600],
    label: "Creator Studio",
  },
  playing: {
    bg: colors.emerald[50],
    color: colors.emerald[600],
    label: "Playing & Buying",
  },
  giftcards: {
    bg: colors.amber[50],
    color: colors.amber[700],
    label: "Gift Cards",
  },
  payouts: {
    bg: colors.cyan[50],
    color: colors.cyan[700],
    label: "Earnings & Payouts",
  },
  default: {
    bg: colors.slate[100],
    color: colors.slate[700],
    label: "Tutorial",
  },
};

const HelpVideoCard = ({ video }) => {
  if (!video) return null;

  const catStyle = CATEGORY_COLORS[video.category] || CATEGORY_COLORS.default;

  const videoUrl = `https://www.youtube.com/watch?v=${video.youtubeId}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(videoUrl);
    toast.success("Video link copied to clipboard!", {
      position: "top-right",
      autoClose: 2000,
    });
  };

  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: customShadows.cardHover,
          borderColor: colors.primary.main,
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* 16:9 Responsive Video Iframe Container */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          paddingTop: "56.25%", // 16:9 Aspect Ratio
          bgcolor: colors.slate[900],
          overflow: "hidden",
        }}
      >
        <iframe
          src={`https://www.youtube.com/embed/${video.youtubeId}`}
          title={video.title}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            border: "none",
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </Box>

      {/* Card Content & Metadata */}
      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", flexGrow: 1 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1.5 }}
        >
          {/* Category Chip */}
          <Box
            sx={{
              px: 1.25,
              py: 0.35,
              borderRadius: borderRadiusPx.sm,
              bgcolor: catStyle.bg,
              color: catStyle.color,
              fontSize: "0.72rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.03em",
            }}
          >
            {video.badge || catStyle.label}
          </Box>

          {/* Quick Actions */}
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Copy YouTube link">
              <IconButton size="small" onClick={handleCopyLink} sx={{ color: colors.slate[400], p: 0.5 }}>
                <Copy size={15} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Watch on YouTube">
              <IconButton
                size="small"
                component="a"
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: colors.slate[400], p: 0.5 }}
              >
                <ExternalLink size={15} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Video Title */}
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 800,
            color: colors.slate[900],
            fontSize: "1rem",
            lineHeight: 1.35,
            mb: 1,
          }}
        >
          {video.title}
        </Typography>

        {/* Video Description */}
        <Typography
          variant="body2"
          sx={{
            color: colors.slate[600],
            fontSize: "0.85rem",
            lineHeight: 1.55,
            flexGrow: 1,
          }}
        >
          {video.description}
        </Typography>
      </Box>
    </Card>
  );
};

HelpVideoCard.propTypes = {
  video: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    youtubeId: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    badge: PropTypes.string,
    description: PropTypes.string.isRequired,
  }).isRequired,
};

export default HelpVideoCard;
